import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Mail, MapPin, MessageSquare } from "lucide-react";
import HeaderV2 from "./HeaderV2";
import FooterV2 from "./FooterV2";
import V2PageHero from "./sections/V2PageHero";
import FAQV2 from "./sections/FAQV2";
import useSmoothScroll from "../../hooks/useSmoothScroll";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { createConsultation } from "../../lib/api";
import { toast } from "sonner";

const SERVICES = [
  { value: "mainland", label: "Mainland Company Formation" },
  { value: "freezone", label: "Freezone Company Formation" },
  { value: "visa", label: "Visa & Residency" },
  { value: "banking", label: "Corporate Bank Account" },
  { value: "other", label: "General Consultation" },
];

const initial = {
  name: "",
  email: "",
  phone: "",
  service: "other",
  message: "",
};

export default function ContactV2() {
  useSmoothScroll(true);
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in name, email and phone.");
      return;
    }
    setSending(true);
    try {
      await createConsultation(form);
      setSubmittedEmail(form.email);
      setDone(true);
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : detail || err?.message || "Something went wrong.";
      toast.error(String(msg));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="v2-contact-page" className="bg-white">
      <HeaderV2 />
      <main>
        <V2PageHero
          eyebrow="Contact"
          title="Talk to a"
          highlight="senior advisor."
          copy="Tell us about your vision. We respond within one business day with a written, transparent roadmap and quote."
          image="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=2400&q=80"
        />

        <section className="relative bg-white py-10 md:py-14">
          <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Left: contact info */}
            <div className="lg:col-span-5 space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6 }}
                className="rounded-sm border border-slate-200 bg-white p-7"
              >
                <div className="w-11 h-11 rounded-sm bg-[#EEF2FF] text-[#3366FF] inline-flex items-center justify-center">
                  <MapPin className="w-5 h-5" />
                </div>
                <p className="overline text-[#3366FF] mt-5">Office</p>
                <p className="mt-2 text-[#0F172A] font-heading font-semibold">
                  Sharjah Media City Free Zone
                </p>
                <p className="text-slate-600 text-sm">Al Messaned, UAE</p>
              </motion.div>

              <motion.a
                href="mailto:contact@startinc.ae"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: 0.05 }}
                className="block rounded-sm border border-slate-200 bg-white p-7 hover:border-[#3366FF]/40 hover:shadow-md transition-all"
              >
                <div className="w-11 h-11 rounded-sm bg-[#EEF2FF] text-[#3366FF] inline-flex items-center justify-center">
                  <Mail className="w-5 h-5" />
                </div>
                <p className="overline text-[#3366FF] mt-5">Email</p>
                <p className="mt-2 text-[#0F172A] font-heading font-semibold">
                  contact@startinc.ae
                </p>
                <p className="text-slate-600 text-sm">Reply within 24 hours.</p>
              </motion.a>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="rounded-sm bg-[#001A4D] text-white p-7"
              >
                <div className="w-11 h-11 rounded-sm bg-white/10 text-[#6B8AFF] inline-flex items-center justify-center">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <p className="overline text-[#6B8AFF] mt-5">Why founders write to us</p>
                <ul className="mt-4 space-y-2.5 text-sm text-slate-300/90">
                  {[
                    "Personalised Mainland vs Freezone roadmap",
                    "Transparent written quote within 24 hours",
                    "Direct introductions to UAE banking RMs",
                    "End-to-end visa & PRO support",
                  ].map((t) => (
                    <li key={t} className="flex gap-3">
                      <span className="w-1 h-1 mt-2 rounded-full bg-[#6B8AFF] shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* Right: form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-7"
            >
              <div className="rounded-sm border border-slate-200 bg-white p-8 md:p-10 shadow-sm">
                <p className="overline text-[#3366FF]">Free consultation</p>
                <h2 className="mt-3 font-heading font-light text-2xl md:text-3xl tracking-tight text-[#0F172A]">
                  Tell us about your{" "}
                  <span className="font-black text-[#002366]">vision</span>.
                </h2>
                <p className="mt-2 text-sm text-slate-600">
                  No obligations — just a 20-minute strategy call with a senior advisor.
                </p>

                <form
                  onSubmit={onSubmit}
                  className="mt-7 space-y-4"
                  data-testid="v2-contact-form"
                >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field
                          testId="v2-form-name"
                          label="Full Name"
                          value={form.name}
                          onChange={(v) => update("name", v)}
                          placeholder="Jane Doe"
                        />
                        <Field
                          testId="v2-form-email"
                          label="Work Email"
                          type="email"
                          value={form.email}
                          onChange={(v) => update("email", v)}
                          placeholder="jane@company.com"
                        />
                      </div>
                      <Field
                        testId="v2-form-phone"
                        label="Phone (with country code)"
                        value={form.phone}
                        onChange={(v) => update("phone", v)}
                        placeholder="+971 50 000 0000"
                      />

                      <div>
                        <label className="overline text-[#0F172A] block mb-2">
                          Service of Interest
                        </label>
                        <Select
                          value={form.service}
                          onValueChange={(v) => update("service", v)}
                        >
                          <SelectTrigger
                            data-testid="v2-form-service"
                            className="w-full bg-white border border-slate-300 text-[#0F172A] rounded-sm h-11 focus:ring-[#3366FF]/40"
                          >
                            <SelectValue placeholder="Select service" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border border-slate-200">
                            {SERVICES.map((s) => (
                              <SelectItem key={s.value} value={s.value}>
                                {s.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="overline text-[#0F172A] block mb-2">
                          Message (optional)
                        </label>
                        <textarea
                          data-testid="v2-form-message"
                          rows={4}
                          value={form.message}
                          onChange={(e) => update("message", e.target.value)}
                          placeholder="Tell us what you want to build in the UAE…"
                          className="w-full rounded-sm bg-white border border-slate-300 text-[#0F172A] placeholder-slate-400 px-3 py-2.5 focus:outline-none focus:border-[#3366FF] focus:ring-2 focus:ring-[#3366FF]/20"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={sending}
                        data-testid="v2-form-submit"
                        className="btn-gold w-full disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {sending ? "Sending…" : "Request my consultation"}
                        {!sending && <ArrowRight className="w-4 h-4" />}
                      </button>

                  <p className="text-xs text-slate-500 text-center">
                    Your information stays private. We never share data with third parties.
                  </p>
                </form>
              </div>
            </motion.div>
          </div>
        </section>

        <FAQV2 />
      </main>
      <FooterV2 />

      {/* Confirmation popup */}
      <Dialog open={done} onOpenChange={(o) => setDone(o)}>
        <DialogContent
          data-testid="v2-contact-success"
          className="max-w-md bg-white border border-slate-200 text-[#0F172A] p-0 overflow-hidden rounded-sm"
        >
          <div className="bg-[#001A4D] text-white p-7 text-center">
            <CheckCircle2 className="w-14 h-14 text-[#6B8AFF] mx-auto" strokeWidth={1.5} />
            <DialogTitle className="mt-5 font-heading font-light text-2xl md:text-3xl tracking-tight text-white">
              Thanks!
            </DialogTitle>
            <DialogDescription className="mt-3 text-slate-200/85">
              We'll be in touch within{" "}
              <span className="font-semibold text-white">24 hours</span>.
            </DialogDescription>
          </div>
          <div className="p-7 text-center">
            <p className="text-sm text-slate-600">
              We've also sent a confirmation to{" "}
              <span className="font-semibold text-[#0F172A]">
                {submittedEmail || "your email"}
              </span>
              .
            </p>
            <button
              data-testid="v2-contact-success-close"
              onClick={() => setDone(false)}
              className="btn-gold mt-7 w-full justify-center"
            >
              Close
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div>
      <label className="overline text-[#0F172A] block mb-2">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-sm bg-white border border-slate-300 text-[#0F172A] placeholder-slate-400 px-3 py-2.5 focus:outline-none focus:border-[#3366FF] focus:ring-2 focus:ring-[#3366FF]/20"
      />
    </div>
  );
}
